import axios, {
    AxiosError,
    AxiosRequestTransformer,
    AxiosResponse,
    AxiosResponseTransformer,
    CreateAxiosDefaults,
} from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'
import { z } from 'zod'
import { refresh } from './user/api'
import { clearStorage, getToken, isTokenExpired } from './auth'
import { notification } from 'antd'
import { buildFilters, Filters } from './hooks/use-pagination'
import i18n, { t } from 'i18next'
import { SearchQuery } from './zod/pagination'
import * as Sentry from '@sentry/browser'

export const baseURL = import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? 'http://localhost/api' : '/api')

export const axiosDefaults: CreateAxiosDefaults = {
    baseURL,
    transformRequest: [
        (data) => {
            const decamelized = decamelizeKeys(data)

            if (typeof decamelized === 'object' && 'custom_attributes' in decamelized) {
                decamelized.custom_attributes = data.customAttributes
            }

            return decamelized
        },
        ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
    ],
    transformResponse: [
        ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
        (data) => {
            const camelized = camelizeKeys(data)

            if (typeof camelized === 'object' && 'customAttributes' in camelized) {
                camelized.customAttributes = data.custom_attributes
            }

            return camelized
        },
    ],
    headers: {
        'Content-Type': 'application/json',
    },
}

export const api = axios.create(axiosDefaults)

api.interceptors.request.use(
    async function (config) {
        // The backend answers in the language on screen, so its validation errors match the form.
        config.headers['X-Locale'] = i18n.language

        const token = getToken('access')

        if (token) {
            config.headers.Authorization = `Bearer ${token}`

            if (isTokenExpired(token)) {
                const token = getToken('refresh')

                if (!token || isTokenExpired(token)) {
                    clearStorage()
                    window.location.reload()
                    throw new Error('refresh expired, auth required')
                }

                const response = await refresh(token)
                config.headers.Authorization = `Bearer ${response[0]}`
            }
        }

        return config
    },
    function (error) {
        return Promise.reject(error)
    }
)

api.interceptors.response.use(
    function (response) {
        return response
    },
    function (error) {
        if (error instanceof AxiosError) {
            if (error.code === 'ERR_NETWORK') {
                notification.warning({
                    description: t('Check you internet connectivity'),
                    message: t('Network unreachable'),
                    key: 'ERR_NETWORK',
                })
            }

            if (error.response?.status === 500) {
                notification.error({
                    type: 'error',
                    description: t('Server error encountered, try again later'),
                    message: t('Server error'),
                    key: '500',
                })
            }

            if (error.response?.status === 429) {
                notification.warning({
                    description: t('You are sending too many requests, wait a bit and try again'),
                    message: t('Too many requests'),
                    key: '429',
                    duration: 2,
                })
            }

            if (error.response?.status === 403) {
                notification.warning({
                    description: t('You do not have permission to perform this action'),
                    message: t('Access Denied'),
                    key: '403',
                    duration: 2,
                })
            }
        }

        return Promise.reject(error)
    }
)

function onError(err: Error): never {
    console.error(err)
    Sentry.captureException(err)

    throw err
}

export async function get<T>(schema: z.ZodType<T>, path: string) {
    return await api
        .get<z.ZodType<T>>(path)
        .then((response) => schema.parse(response.data))
        .catch(onError)
}

export async function post<T, D>(schema: z.ZodType<T>, path: string, data?: D) {
    return await api
        .post<z.ZodType<T>, AxiosResponse<z.ZodType<T>>, D>(path, data)
        .then((response) => schema.parse(response.data))
        .catch(onError)
}

export async function put<T, D>(schema: z.ZodType<T>, path: string, data: D) {
    return await api
        .put(path, data)
        .then((response) => schema.parse(response.data))
        .catch(onError)
}

export async function patch<T, D>(schema: z.ZodType<T>, path: string, data?: D) {
    return await api
        .patch<z.ZodType<T>, AxiosResponse<z.ZodType<T>>, D>(path, data || ({} as D))
        .then((response) => schema.parse(response.data))
        .catch(onError)
}

export async function del<T>(schema: z.ZodType<T>, path: string) {
    return await api
        .delete<z.ZodType<T>>(path)
        .then((response) => schema.parse(response.data))
        .catch(onError)
}

export function toQueryString({ filters, ...rest }: SearchQuery) {
    return new URLSearchParams({
        ...(decamelizeKeys(rest) as Record<string, string>),
        ...(filters ? buildFilters(decamelizeKeys(filters) as Filters) : {}),
    }).toString()
}
